import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription
} from '@/components/ui/drawer';
import { formatPriceDisplay } from '@/util/formatting';
import { getServiceUnitPrice } from '@/util/service-pricing';
import type { ServiceType } from '@/components/ui/carousel';
import { sortAssetServices } from '../assets-action-bar/AssetServicePills';

type AssetServicesSheetProps = {
  services: ServiceType[];
  selectedCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPickService: (service: ServiceType) => void;
};

// Sub-lg services entry point (2026-08-19 design update, absorbs the sub-lg
// half of spec §2.3): the selection bar's Services action opens this sheet;
// picking a row hands off to the existing pickedService → OrderServiceModal
// flow, wiring unchanged. Desktop keeps the control row's AssetServicePills,
// so this surface is mounted below lg only (see AssetsSelectionBar).
const AssetServicesSheet = ({
  services,
  selectedCount,
  open,
  onOpenChange,
  onPickService
}: AssetServicesSheetProps) => (
  <Drawer open={open} onOpenChange={onOpenChange}>
    <DrawerContent data-testid="asset-services-sheet" className="max-h-[80dvh]">
      <DrawerHeader>
        <DrawerTitle>Add Services</DrawerTitle>
        <DrawerDescription>
          Applies to {selectedCount} selected{' '}
          {selectedCount === 1 ? 'photo' : 'photos'}
        </DrawerDescription>
      </DrawerHeader>
      <div className="flex flex-col overflow-y-auto pb-6">
        {sortAssetServices(services).map((service) => {
          const unit = getServiceUnitPrice(service);
          return (
            <button
              key={service.uuid}
              type="button"
              onClick={() => {
                // Close first: the OrderServiceModal the pick opens is a
                // Radix dialog of its own, and leaving the sheet mounted
                // underneath would stack two focus traps.
                onOpenChange(false);
                onPickService(service);
              }}
              className="flex min-h-12 cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-left"
            >
              <span className="flex-1 truncate text-sm font-medium text-foreground">
                {service.name}
              </span>
              <span className="shrink-0 text-sm text-muted-foreground">
                {unit === 0 ? 'Free' : `$${formatPriceDisplay(unit)} each`}
              </span>
            </button>
          );
        })}
      </div>
    </DrawerContent>
  </Drawer>
);

export default AssetServicesSheet;
